import { useState } from "react";
import { Button, Card, CardBody, CardHeader, Chip, Divider, Select, SelectItem, Snippet } from "@heroui/react";
import { useRun } from "../lib/state.tsx";
import { Json } from "../components/Json.tsx";
import { Section } from "../components/Section.tsx";

interface WireResult {
  protocol: string;
  sessionId: string | null;
  connect: Record<string, unknown>;
  toolCount: number;
  call: { name: string; args: unknown; result: unknown };
  requestBytes: number;
  responseBytes: number;
}

interface ElicitResult {
  first: { resultType?: string; inputRequests?: Record<string, unknown>; requestState?: string };
  retry?: Record<string, unknown>;
  tampered?: Record<string, unknown>;
}

const PROTOCOLS = [
  { key: "2026-07-28", label: "2026-07-28 — stateless" },
  { key: "2025-06-18", label: "2025-06-18 — handshake + session" },
];

export function WirePanel() {
  const [protocol, setProtocol] = useState("2026-07-28");
  const wire = useRun<WireResult>();
  const elicit = useRun<ElicitResult>();
  const modern = protocol === "2026-07-28";

  return (
    <div className="flex flex-col gap-4">
      <Section
        title="One call, two protocols"
        blurb="The same tool, the same core, the same answer. Everything that differs is envelope. Watch the wire log as you switch."
      >
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="Protocol"
            size="sm"
            className="max-w-xs"
            selectedKeys={[protocol]}
            onSelectionChange={(keys) => setProtocol([...keys][0] as string)}
          >
            {PROTOCOLS.map((p) => (
              <SelectItem key={p.key}>{p.label}</SelectItem>
            ))}
          </Select>
          <Button
            color="primary"
            isLoading={wire.pending}
            onPress={() => void wire.run("wire", { protocol })}
          >
            Run search_notes
          </Button>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Card shadow="none" className="border border-default-100/60 bg-default-50/30">
            <CardHeader className="pb-1 text-sm font-medium">
              {modern ? "What this version does" : "What this version did"}
            </CardHeader>
            <CardBody className="pt-0 text-xs text-default-400">
              {modern ? (
                <ul className="list-inside list-disc space-y-1">
                  <li>
                    No <code className="text-primary">initialize</code>. The first request is the
                    request.
                  </li>
                  <li>
                    No <code className="text-primary">Mcp-Session-Id</code>. Any instance can serve
                    any call.
                  </li>
                  <li>
                    Identity and capabilities ride in <code className="text-warning">_meta</code> on
                    every request.
                  </li>
                  <li>
                    <code className="text-warning">MCP-Protocol-Version</code>,{" "}
                    <code className="text-warning">Mcp-Method</code> and{" "}
                    <code className="text-warning">Mcp-Name</code> are mirrored into headers so a
                    gateway can route without parsing the body.
                  </li>
                  <li>
                    Every result carries <code className="text-warning">resultType</code>; lists
                    carry <code className="text-warning">ttlMs</code> and{" "}
                    <code className="text-warning">cacheScope</code>.
                  </li>
                </ul>
              ) : (
                <ul className="list-inside list-disc space-y-1">
                  <li>
                    A mandatory <code className="text-secondary">initialize</code> →{" "}
                    <code className="text-secondary">notifications/initialized</code> handshake.
                  </li>
                  <li>
                    Capabilities exchanged <em>once</em>, then remembered against a session id.
                  </li>
                  <li>
                    Every later request carries{" "}
                    <code className="text-secondary">Mcp-Session-Id</code> — so the load balancer
                    has to be sticky.
                  </li>
                  <li>A standalone GET SSE stream for server-initiated requests.</li>
                  <li>
                    No <code>resultType</code>, no cache hints. A client cannot tell a final result
                    from an interim one without looking inside.
                  </li>
                </ul>
              )}
            </CardBody>
          </Card>

          <Card shadow="none" className="border border-default-100/60 bg-default-50/30">
            <CardHeader className="pb-1 text-sm font-medium">Result</CardHeader>
            <CardBody className="pt-0">
              {wire.error && <p className="text-xs text-danger">{wire.error}</p>}
              {!wire.data && !wire.error && (
                <p className="text-xs text-default-500">Run it to see the frames.</p>
              )}
              {wire.data && (
                <>
                  <div className="mb-2 flex flex-wrap gap-2">
                    <Chip size="sm" variant="flat" color={modern ? "primary" : "secondary"}>
                      {wire.data.protocol}
                    </Chip>
                    <Chip size="sm" variant="flat">
                      {wire.data.toolCount} tools
                    </Chip>
                    <Chip size="sm" variant="flat">
                      {wire.data.requestBytes} B out · {wire.data.responseBytes} B in
                    </Chip>
                    <Chip
                      size="sm"
                      variant="flat"
                      color={wire.data.sessionId ? "warning" : "success"}
                    >
                      {wire.data.sessionId
                        ? `session ${wire.data.sessionId.slice(0, 8)}…`
                        : "no session"}
                    </Chip>
                  </div>
                  <Json value={wire.data.call.result} className="max-h-64 overflow-y-auto" />
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </Section>

      <Section
        title="Multi round-trip requests"
        blurb="The server needs a target environment before it will publish, and in 2026-07-28 it has no channel to ask on. So it answers with a result that says input_required, and the client retries the same call carrying the answer."
      >
        <Button color="primary" isLoading={elicit.pending} onPress={() => void elicit.run("elicit")}>
          Run deploy_note
        </Button>

        {elicit.data && (
          <div className="mt-4 flex flex-col gap-3">
            <div className="grid gap-3 md:grid-cols-3">
              <Card shadow="none" className="border border-warning/40 bg-warning/5">
                <CardHeader className="pb-1 text-xs font-medium text-warning">
                  1 · server answers input_required
                </CardHeader>
                <CardBody className="pt-0">
                  <Json value={elicit.data.first} className="max-h-56 overflow-y-auto" />
                </CardBody>
              </Card>
              <Card shadow="none" className="border border-success/40 bg-success/5">
                <CardHeader className="pb-1 text-xs font-medium text-success">
                  2 · client retries with inputResponses
                </CardHeader>
                <CardBody className="pt-0">
                  <Json value={elicit.data.retry} className="max-h-56 overflow-y-auto" />
                </CardBody>
              </Card>
              <Card shadow="none" className="border border-danger/40 bg-danger/5">
                <CardHeader className="pb-1 text-xs font-medium text-danger">
                  3 · a tampered requestState
                </CardHeader>
                <CardBody className="pt-0">
                  <Json value={elicit.data.tampered} className="max-h-56 overflow-y-auto" />
                </CardBody>
              </Card>
            </div>

            <Divider />
            <p className="text-xs text-default-400">
              The spec is explicit that <code className="text-warning">requestState</code> is
              attacker-controlled input. Ours is HMAC-signed and carries the principal, a
              five-minute expiry, the method, and a digest of the arguments — so it cannot be
              replayed against a different call, a different agent, or a different environment.
            </p>
          </div>
        )}
      </Section>

      <Section
        title="Point a real client at it"
        blurb="This is a real MCP server, not a mock. Any 2026-07-28 client can list and call these tools."
      >
        <Snippet size="sm" className="w-full font-mono" symbol="">
          {typeof window !== "undefined" ? `${window.location.origin}/mcp` : "/mcp"}
        </Snippet>
      </Section>
    </div>
  );
}
